import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import PendingAcksWidget from "@/components/PendingAcksWidget";
import OnboardingChecklistWidget from "@/components/OnboardingChecklistWidget";
import EnablePushButton from "@/components/EnablePushButton";
import ExpiringClearancesWidget from "@/components/ExpiringClearancesWidget";
import ExpiringDocumentsWidget from "@/components/ExpiringDocumentsWidget";
import InquiriesStatsTile from "@/components/InquiriesStatsTile";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Building2, School, ArrowRight, CalendarRange, Users, BookOpen,
  Sparkles, AlertCircle, Settings,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/* Stat card                                                                  */
/* -------------------------------------------------------------------------- */
function Stat({ icon: Icon, label, value, tint, testId }) {
  // tint provides the pastel icon background + matching icon color
  return (
    <div
      className="bg-white rounded-2xl border border-[#2563EB]/10 shadow-floating hover:shadow-floating-hover hover:-translate-y-0.5 transition-all duration-200 p-5"
      data-testid={testId}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${tint}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-slate-500 font-body">{label}</div>
          <div className="font-heading text-2xl font-bold text-foreground tracking-tight mt-1 truncate">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Feature card                                                               */
/* -------------------------------------------------------------------------- */
function FeatureCard({ icon: Icon, title, description, to, testId }) {
  return (
    <Link
      to={to}
      className="block bg-white rounded-2xl border border-[#2563EB]/10 shadow-floating card-lift p-6"
      data-testid={testId}
    >
      <Icon className="w-8 h-8 text-[#2563EB] mb-4" strokeWidth={1.6} />
      <h3 className="font-heading text-lg font-bold text-foreground tracking-tight">{title}</h3>
      <p className="text-sm text-slate-500 mt-2 leading-relaxed">{description}</p>
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/* Main page                                                                  */
/* -------------------------------------------------------------------------- */
export default function OwnerOverviewPage() {
  const { user } = useAuth();
  const { schools, selectedSchool, selectedBranch, loading } = useBranch();
  const [stats, setStats] = useState({ classes: 0, students: 0, activeYear: null });
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    if (!selectedBranch) {
      setStats({ classes: 0, students: 0, activeYear: null });
      return;
    }
    let cancelled = false;
    (async () => {
      setStatsLoading(true);
      const isStaff = user?.role === "branch_admin" || user?.role === "special_access";
      const perms = user?.permissions || {};
      const canViewClasses = !isStaff || perms.classes_view || perms.students_view;
      const canViewStudents = !isStaff || perms.students_view;
      try {
        const results = await Promise.allSettled([
          canViewClasses ? api.get("/classes", { params: { branch_id: selectedBranch.id } }) : Promise.resolve({ data: [] }),
          canViewStudents ? api.get("/students", { params: { branch_id: selectedBranch.id } }) : Promise.resolve({ data: [] }),
          api.get("/school-years/active", { params: { branch_id: selectedBranch.id } }),
        ]);
        if (!cancelled) {
          const get = (i) => results[i].status === "fulfilled" ? results[i].value.data : null;
          setStats({
            classes: (get(0) || []).length,
            students: (get(1) || []).length,
            activeYear: get(2),
          });
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedBranch, user?.role]);

  const firstName = user?.name?.split(" ")[0] || "there";

  return (
    <>
      {/* Welcome */}
      <div className="mb-8" data-testid="owner-overview">
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground tracking-tight leading-tight">
          Welcome back, <span className="text-[#2563EB]">{firstName}!</span>
        </h1>
        <p className="text-base sm:text-lg text-slate-500 mt-3">
          {selectedBranch
            ? <>Viewing <span className="font-semibold text-foreground">{selectedBranch.name}</span> · {selectedSchool?.name}</>
            : selectedSchool
              ? <>Viewing <span className="font-semibold text-foreground">{selectedSchool.name}</span> — pick a branch above to dive in.</>
              : "Register or select a school to get started."}
        </p>
      </div>

      <div className="mb-6"><PendingAcksWidget /></div>
      <div className="mb-6"><EnablePushButton /></div>
      <div className="mb-6"><OnboardingChecklistWidget /></div>

      {loading ? (
        <div className="text-slate-500 font-heading">Loading...</div>
      ) : schools.length === 0 ? (
        <NoSchoolCard />
      ) : !selectedSchool ? (
        <NoSelectionCard message="Select a school from the dropdown above to view its dashboard." />
      ) : !selectedBranch ? (
        <NoSelectionCard message="Select a branch from the dropdown above to view its records." showBranchesLink />
      ) : (
        <>
          {/* Selected branch summary */}
          <div
            className="bg-white rounded-2xl border border-[#2563EB]/15 shadow-floating p-6 mb-6"
            data-testid="overview-branch-card"
          >
            <div className="flex flex-wrap items-center gap-4 justify-between">
              <div className="flex items-start gap-4 min-w-0">
                <div className="w-14 h-14 rounded-2xl bg-[#DBEAFE] flex items-center justify-center shrink-0">
                  <Building2 className="w-7 h-7 text-[#2563EB]" strokeWidth={1.8} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="font-heading text-2xl font-bold text-foreground tracking-tight">{selectedBranch.name}</h2>
                    <StatusBadge status={selectedBranch.status} />
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{selectedBranch.address}</p>
                  <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-1.5">
                    <School className="w-3.5 h-3.5" /> {selectedSchool.name}
                  </p>
                </div>
              </div>
              <Link to={`/branches/${selectedBranch.id}`}>
                <Button
                  className="rounded-full h-11 px-5 bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white shadow-[0_10px_24px_-12px_rgba(37,99,235,0.55)] hover:shadow-[0_14px_28px_-12px_rgba(37,99,235,0.7)] transition-all"
                  data-testid="overview-manage-branch-btn"
                >
                  Open branch <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
            <Stat
              icon={CalendarRange}
              label="Active school year"
              value={statsLoading ? "..." : (stats.activeYear?.label || "None")}
              tint={stats.activeYear ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-[#FFF3E0] text-[#E65100]"}
              testId="stat-active-year"
            />
            <Stat
              icon={BookOpen}
              label="Classes"
              value={statsLoading ? "..." : stats.classes}
              tint="bg-[#DBEAFE] text-[#2563EB]"
              testId="stat-classes"
            />
            <Stat
              icon={Users}
              label="Students"
              value={statsLoading ? "..." : stats.students}
              tint="bg-[#EDE9FE] text-[#7C3AED]"
              testId="stat-students"
            />
          </div>

          {/* If no active year - prompt */}
          {!statsLoading && !stats.activeYear && (
            <div
              className="bg-[#FFF8E1] border border-[#FFB74D]/40 rounded-2xl p-5 mb-6 flex flex-wrap items-center justify-between gap-3"
              data-testid="no-active-year-banner"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center shrink-0 shadow-sm">
                  <AlertCircle className="w-5 h-5 text-[#E65100]" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-foreground">No active school year for this branch</p>
                  <p className="text-sm text-slate-500">
                    Activate a school year in Settings before enrolling students or creating classes & events.
                  </p>
                </div>
              </div>
              <Link to="/settings">
                <Button className="rounded-full bg-[#FFB74D] hover:bg-[#FFA726] text-foreground shadow-sm">
                  <Settings className="w-4 h-4 mr-1" /> Go to Settings
                </Button>
              </Link>
            </div>
          )}

          {/* Feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard
              icon={BookOpen}
              title="Classes & Students"
              description="Manage classes, teachers, and students for this branch."
              to={`/branches/${selectedBranch.id}`}
              testId="feature-card-classes"
            />
            <FeatureCard
              icon={CalendarRange}
              title="School Year"
              description="Create, activate or rotate school years for this branch."
              to="/settings"
              testId="feature-card-school-year"
            />
            <FeatureCard
              icon={Building2}
              title="All Branches"
              description={`Branches of ${selectedSchool.name}.`}
              to="/branches"
              testId="feature-card-branches"
            />
          </div>

          {/* Expiring clearances widget — auto-hides when caller has no perm or no rows */}
          <ExpiringClearancesWidget />

          {/* Expiring branch documents widget — auto-hides when caller has no perm or no rows */}
          <ExpiringDocumentsWidget />

          {/* Inquiries stats tile — auto-hides on 403 */}
          <div className="mt-6"><InquiriesStatsTile /></div>
        </>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Empty states                                                               */
/* -------------------------------------------------------------------------- */
function NoSchoolCard() {
  return (
    <div className="bg-white rounded-2xl border border-[#2563EB]/15 shadow-floating p-8 max-w-2xl" data-testid="overview-no-school">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#DBEAFE] flex items-center justify-center shrink-0">
          <Sparkles className="w-6 h-6 text-[#2563EB]" />
        </div>
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground tracking-tight">Let's register your first school</h2>
          <p className="text-slate-500 mt-1">
            Your dashboard unlocks once your school is registered and approved by our Super Admin team.
          </p>
          <Link to="/school-profile" className="inline-block mt-5">
            <Button
              className="rounded-full h-11 px-6 bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white shadow-[0_10px_24px_-12px_rgba(37,99,235,0.55)]"
              data-testid="overview-register-school-btn"
            >
              Register my school <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function NoSelectionCard({ message, showBranchesLink }) {
  return (
    <div className="bg-white rounded-2xl border border-[#2563EB]/15 shadow-floating p-8 max-w-2xl" data-testid="no-selection">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#FFF3E0] flex items-center justify-center shrink-0">
          <AlertCircle className="w-6 h-6 text-[#E65100]" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground tracking-tight">Nothing to show yet</h2>
          <p className="text-slate-500 mt-1">{message}</p>
          {showBranchesLink && (
            <Link to="/branches" className="inline-block mt-4">
              <Button className="rounded-full bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white shadow-[0_10px_24px_-12px_rgba(37,99,235,0.55)]">
                Add a branch <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
